import { useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

const useNextDisplayAction = ({ game, steps, pageStep, updateStep, changeStep }) => {
    const intl = useIntl();

    const { questions = null } = game || {};
    const { id: pageGameSlug = null } = game || {};
    const { id: stepId = null, component = null } = pageStep || {};

    let nextAction = null;
    let nextLabel = intl.formatMessage({ defaultMessage: 'Next', description: 'Button label' });

    const currentStepIndex = (steps || []).findIndex((s) => s.id === stepId);
    const hasPincodes = (steps || []).find((s) => s.id === 'pincode');
    const nextStep = steps[currentStepIndex + 1] || null;
    const { id: nextStepId = null } = nextStep || {};

    const { id: pageStepId = null } = pageStep || {};
    const stepParts = pageStepId !== null ? pageStepId.split('-') : null;
    const questionId = stepParts !== null && stepParts.length > 1 ? stepParts[1] : null;

    const currentQuestion = (questions || []).find((q) => q.id === questionId) || null;
    const { endAt = null, isStarted = false, expired = false } = currentQuestion || {};
    const running = isStarted && !expired;
    const waiting = !isStarted && !expired;

    const isLast = parseInt(questionId, 10) === (questions || []).length;

    const [locked, setLocked] = useState(false);

    useEffect(() => {
        let id = null;
        if (locked) {
            id = setTimeout(() => {
                setLocked(false);
            }, 3000);
        }
        return () => {
            clearTimeout(id);
        };
    }, [setLocked, locked]);

    const goToStep = useCallback(
        (step) => {
            changeStep(step, null);
            setLocked(true);
        },
        [changeStep, setLocked],
    );

    const goToGameStep = useCallback(
        (step) => {
            changeStep(step, pageGameSlug);
            setLocked(true);
        },
        [changeStep, pageGameSlug, setLocked],
    );

    const finalUpdateStep = useCallback(
        (id, action) => {
            if (updateStep !== null) {
                updateStep(id, action);
                setLocked(true);
            }
        },
        [updateStep],
    );

    switch (component) {
        case 'start':
            nextAction = () => {
                if (pageGameSlug !== null) {
                    goToGameStep('games');
                } else {
                    goToStep('games');
                }
            };
            break;
        case 'games':
            nextLabel = intl.formatMessage({
                defaultMessage: 'Select challenge',
                description: 'Button label',
            });
            nextAction = pageGameSlug !== null ? () => goToGameStep('wait') : null;
            break;
        case 'wait':
            if (hasPincodes) {
                nextLabel = intl.formatMessage({
                    defaultMessage: 'Show code',
                    description: 'Button label',
                });
                nextAction = pageGameSlug !== null ? () => goToGameStep('pincode') : null;
            } else {
                nextLabel = intl.formatMessage({
                    defaultMessage: 'Start challenge',
                    description: 'Button label',
                });
                nextAction = pageGameSlug !== null ? () => goToGameStep('question-1') : null;
            }

            break;
        case 'pincode':
            nextLabel = intl.formatMessage({
                defaultMessage: 'Start challenge',
                description: 'Button label',
            });
            nextAction = pageGameSlug !== null ? () => goToGameStep('question-1') : null;
            break;
        case 'question':
            // if (running && !ended) {
            //     nextLabel = intl.formatMessage({
            //         defaultMessage: 'End question',
            //         description: 'Button label',
            //     });
            //     nextAction = nextStep !== null ? () => finalUpdateStep(stepId, 'end') : null;
            // }
            if (endAt !== null && nextStepId === 'end') {
                nextLabel = intl.formatMessage({
                    defaultMessage: 'Final leaderboard',
                    description: 'Button label',
                });
                nextAction = pageGameSlug !== null ? () => goToGameStep('end') : null;
            } else if (endAt !== null) {
                nextLabel = intl.formatMessage({
                    defaultMessage: 'Next question',
                    description: 'Button label',
                });
                nextAction =
                    questionId !== null
                        ? () => goToGameStep(`question-${parseInt(questionId, 10) + 1}`)
                        : null;
            } else {
                nextLabel = intl.formatMessage({
                    defaultMessage: 'Launch question',
                    description: 'Button label',
                });
                nextAction = nextStep !== null ? () => finalUpdateStep(stepId, 'start') : null;
            }
            break;
        case 'leaderboard':
            if (isLast) {
                nextLabel = intl.formatMessage({
                    defaultMessage: 'Final leaderboard',
                    description: 'Button label',
                });
            } else {
                nextLabel = intl.formatMessage({
                    defaultMessage: 'Next question',
                    description: 'Button label',
                });
            }
            nextAction = nextStep !== null ? () => goToGameStep(nextStep.id) : null;
            break;
        case 'end':
            nextLabel = intl.formatMessage({
                defaultMessage: 'Finish',
                description: 'Button label',
            });
            nextAction =
                pageGameSlug !== null
                    ? () => updateStep(stepId, 'game_end').then(() => goToStep('start'))
                    : null;
            break;
        default:
            break;
    }

    const onClickNext = useCallback((...props) => nextAction(...props), [nextAction]);

    return {
        onClickNext: nextAction !== null ? onClickNext : null,
        nextLabel,
        locked,
        running,
        waiting,
    };
};

export default useNextDisplayAction;
